import { rotateArray } from "$utils/array";
import { nanoid } from "nanoid";

export const sample = (arr, sampleSize=1) => {
    if (sampleSize > arr.length) {
        throw new Error("Sample size cannot be larger than the array size");
    }

    const sampledItems = new Set();
    
    while (sampledItems.size < sampleSize) {
        const randomIndex = Math.floor(Math.random() * arr.length);
        sampledItems.add(arr[randomIndex]);
    }
    
    if (sampleSize === 1) return [...sampledItems][0];
    return [...sampledItems];
}

// based on https://github.com/trekhleb/javascript-algorithms/tree/master/src/algorithms/statistics/weighted-random
export const weightedSample = (items, weights) => {
    if (items.length !== weights.length) throw new Error('Items and weights must be of the same size');
    if (!items.length) throw new Error('Items must not be empty');

    const totalWeight = weights.reduce((sum, weight) => sum + weight, 0); // Calculate the total sum of weights.
    const threshold = totalWeight * Math.random();
  
    let cumulative = 0;
    for (let i = 0; i < items.length; i++) {
      cumulative += weights[i];
      if (cumulative >= threshold) return items[i];
    }
}

export const roundToStep = (value, step) => {
    if (step === undefined || step === 0) return value;
    return Math.round(value / step) * step;
}

// Make sure numeric value is within min/max boundaries
export const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
export const lerp = (value1, value2, t) => value1 + (value2-value1) * t;
export const normalize = (value, min, max) => (value - min) / (max - min);
export const scale = (value, inmin, inmax, outmin, outmax) => lerp(outmin, outmax, normalize(value, inmin, inmax));

// Trigonometry
export const degToRad = deg => deg / 180 * Math.PI;
export const polToCar = ({r, a}) => ({x: r * Math.cos(a), y: r * Math.sin(a)});


export const distanceToAbsolute = (distanceArray, startOffset=0) => {
    return distanceArray.reduce((absoluteArray, distanceValue, index) => {
        const absoluteValue = distanceValue + (absoluteArray[index]);
        absoluteArray.push(absoluteValue);
        return absoluteArray;
    }, [startOffset])
}

export const absoluteToDistance = (absoluteArray) => {
    return absoluteArray.slice(1).reduce((distanceArray, absoluteValue, index) => {
        const distanceValue = absoluteValue - absoluteArray[index];
        distanceArray.push(distanceValue);
        return distanceArray;
    }, []);
}

// the good old http://cgm.cs.mcgill.ca/~godfried/publications/banff.pdf
export const euclid = (pulses, steps, offset=0) => {
    if (pulses > steps) throw new Error(`More pulses (${pulses}) than steps (${steps})!`);

    const bjorklundArray = Array(steps);
    let lastTruncated = 0;
    for (let i = 1; i <= steps; i++) {	
        const truncatedValue = Math.floor((i * pulses)/steps);
        const bjorklundValue = truncatedValue - lastTruncated;
        lastTruncated = truncatedValue;
        
        const index = (i==steps) ? 0 : i;	// puts the last element first
        bjorklundArray[index] = bjorklundValue;
    }

    const rotatedBjorklund = rotateArray(bjorklundArray, offset);
    const euclidAbsolute = rotatedBjorklund.map((value, index)=>value == 1 ? value*index : undefined).filter(item=>item!==undefined);
    const euclidArray = absoluteToDistance([...euclidAbsolute, steps]);
    return euclidArray;
}

export const makeUID = (prefix) => {
    let id = "";
    if (prefix != null) id += prefix + "-"
    id += nanoid()
    return id;
}

// Quantize functions
export const quantizeFloor = (value, step) => Math.floor(value / step) * step;
export const quantizeRound = (value, step) => Math.round(value / step) * step;
export const quantizeCeil = (value, step) => Math.ceil(value / step) * step;